import api from '../utils/api';

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: 'admin' | 'manager' | 'employee';
  department?: {
    id: number;
    name: string;
    description: string;
  };
  location?: {
    id: number;
    name: string;
    address: string;
  };
  employment_status: 'active' | 'inactive' | 'terminated';
  hire_date?: string;
  phone_number: string;
  timezone: string;
  employee_id?: string; // Employee ID - unique identifier
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  company?: {
    id: number;
    name: string;
  };
  managed_departments?: any[];
  managed_locations?: any[];
  managed_employees?: any[];
  created_at: string;
  updated_at: string;
  last_login_at?: string;
}

export interface Department {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

export interface Location {
  id: number;
  name: string;
  address: string;
  is_active: boolean;
}

export interface LoginResponse {
  user: User;
  tokens: {
    access: string;
    refresh: string;
  };
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  user?: User;
}

class AuthService {
  private readonly TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';

  // Token management
  setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  setRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  removeToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    // Also clear company switcher context for superusers
    localStorage.removeItem('activeCompanyId');
  }

  // Authentication methods
  async login(email: string, password: string): Promise<ApiResponse<LoginResponse>> {
    try {
      const response = await api.post('/auth/login/', {
        email,
        password,
      });

      if (response.data) {
        const { user, tokens } = response.data;
        this.setToken(tokens.access);
        this.setRefreshToken(tokens.refresh);
        
        // Set default Authorization header for future requests
        api.defaults.headers.common['Authorization'] = `Bearer ${tokens.access}`;

        return {
          success: true,
          data: response.data,
          user,
        };
      }

      return { success: false, error: 'Invalid response format' };
    } catch (error: any) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 
               error.response?.data?.non_field_errors?.[0] ||
               'Login failed',
      };
    }
  }

  async logout(): Promise<void> {
    try {
      const refreshToken = this.getRefreshToken();
      if (refreshToken) {
        await api.post('/auth/logout/', {
          refresh_token: refreshToken,
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.removeToken();
      delete api.defaults.headers.common['Authorization'];
    }
  }

  async verifyToken(): Promise<User | null> {
    try {
      const token = this.getToken();
      if (!token) return null;

      // Set Authorization header
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get('/auth/verify/');
      return response.data?.user || null;
    } catch (error) {
      console.error('Token verification error:', error);
      this.removeToken();
      delete api.defaults.headers.common['Authorization'];
      return null;
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await api.get('/users/me/');
      return response.data;
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }

  async updateProfile(userData: Partial<User>): Promise<ApiResponse<User>> {
    try {
      const response = await api.patch('/users/me/', userData);
      return {
        success: true,
        user: response.data,
      };
    } catch (error: any) {
      console.error('Update profile error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Profile update failed',
      };
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse> {
    try {
      await api.post('/users/change_password/', {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: newPassword,
      });

      return { success: true };
    } catch (error: any) {
      console.error('Change password error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 
               error.response?.data?.current_password?.[0] ||
               error.response?.data?.new_password?.[0] ||
               'Password change failed',
      };
    }
  }

  async requestPasswordReset(email: string): Promise<ApiResponse> {
    try {
      await api.post('/auth/password-reset/', { email });
      return { success: true };
    } catch (error: any) {
      console.error('Password reset request error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 
               error.response?.data?.email?.[0] ||
               'Password reset request failed',
      };
    }
  }

  async confirmPasswordReset(token: string, password: string): Promise<ApiResponse> {
    try {
      await api.post('/auth/password-reset/confirm/', {
        token,
        password,
        password_confirm: password,
      });
      return { success: true };
    } catch (error: any) {
      console.error('Password reset confirm error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 
               error.response?.data?.password?.[0] ||
               'Password reset failed',
      };
    }
  }

  async acceptInvitation(
    token: string, 
    password: string
  ): Promise<ApiResponse<LoginResponse>> {
    try {
      const response = await api.post('/users/accept-invitation/', {
        token,
        password,
        password_confirm: password,
      });

      if (response.data) {
        const { user, tokens } = response.data;
        this.setToken(tokens.access);
        this.setRefreshToken(tokens.refresh);
        
        // Set default Authorization header for future requests
        api.defaults.headers.common['Authorization'] = `Bearer ${tokens.access}`;

        return {
          success: true,
          data: response.data,
          user,
        };
      }

      return { success: false, error: 'Invalid response format' };
    } catch (error: any) {
      console.error('Accept invitation error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 
               error.response?.data?.non_field_errors?.[0] ||
               'Account creation failed',
      };
    }
  }

  // Helper methods for getting user info
  isAdmin(user?: User): boolean {
    return user?.role === 'admin' || false;
  }

  isManager(user?: User): boolean {
    return user?.role === 'manager' || false;
  }

  isEmployee(user?: User): boolean {
    return user?.role === 'employee' || false;
  }

  canManageUsers(user?: User): boolean {
    return this.isAdmin(user);
  }

  canViewAdminDashboard(user?: User): boolean {
    return this.isAdmin(user) || this.isManager(user);
  }
}

// Export singleton instance
export const authService = new AuthService();

// Department and Location services
export const departmentService = {
  async getAll(): Promise<Department[]> {
    try {
      const response = await api.get('/departments/');
      const data = response.data;
      if (Array.isArray(data)) return data as Department[];
      if (data && Array.isArray(data.results)) return data.results as Department[];
      return [];
    } catch (error) {
      console.error('Get departments error:', error);
      return [];
    }
  },

  async create(department: Omit<Department, 'id'>): Promise<ApiResponse<Department>> {
    try {
      const response = await api.post('/departments/', department);
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to create department',
      };
    }
  },
};

export const locationService = {
  async getAll(): Promise<Location[]> {
    try {
      const response = await api.get('/locations/');
      const data = response.data;
      if (Array.isArray(data)) return data as Location[];
      if (data && Array.isArray(data.results)) return data.results as Location[];
      return [];
    } catch (error) {
      console.error('Get locations error:', error);
      return [];
    }
  },

  async create(location: Omit<Location, 'id'>): Promise<ApiResponse<Location>> {
    try {
      const response = await api.post('/locations/', location);
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to create location',
      };
    }
  },
};

export default authService;